import { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { reilmAPI } from '../../services/apiClient'

const STORAGE_KEY = 'reilm.auth'

const AuthContext = createContext(null)

const extractApiError = (err, fallback) => {
  const serverMessage = err?.response?.data?.error?.message
  return new Error(serverMessage || err?.message || fallback)
}

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  })

  useEffect(() => {
    if (session) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  }, [session])

  const login = async ({ email, password }) => {
    if (!email || !password) {
      throw new Error('Email and password are required')
    }
    try {
      const response = await reilmAPI.loginUser({ email, password })
      const { user, accessToken } = response.data?.data ?? {}
      if (!user || !accessToken) {
        throw new Error('Unexpected response from server')
      }
      const next = { user, accessToken }
      setSession(next)
      return next
    } catch (err) {
      throw extractApiError(err, 'Unable to sign in')
    }
  }

  const signup = async ({ email, password }) => {
    if (!email || !password) {
      throw new Error('Email and password are required')
    }
    try {
      const response = await reilmAPI.createUser({ email, password })
      const { user, accessToken } = response.data?.data ?? {}
      if (!user || !accessToken) {
        throw new Error('Unexpected response from server')
      }
      const next = { user, accessToken }
      setSession(next)
      return next
    } catch (err) {
      throw extractApiError(err, 'Unable to create account')
    }
  }

  const logout = () => setSession(null)

  const value = useMemo(
    () => ({
      user: session?.user ?? null,
      accessToken: session?.accessToken ?? null,
      isAuthenticated: !!session,
      login,
      signup,
      logout,
    }),
    [session]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
